import React, { useContext, useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import AuthContext from "../context/userAuth.context";
import {
  SECTIONS,
  adminLoadPosts,
  adminPublishPost,
  adminSavePost,
  emptyPost,
  postHasContent,
} from "../Utils/whatsNew";
import WhatsNewPostView from "../dashboard.component/dashboard.rightarea.component/WhatsNewPostView";

// Admin editor for the "What's new" sidebar: write updates and news, preview them exactly as
// customers will see them, publish or hide them. Opened from the Admin Panel, or with "Manage"
// in the What's new sidebar (admins only). Nothing is deleted: "Unpublish" hides a post.

const BLUE = "#296AFF";
const SECTION_HINTS = {
  new: "Brand-new features",
  improved: "Things that work better",
  fixed: "Problems that are solved",
};

const label = { display: "block", fontSize: 12, fontWeight: 600, color: "#5b6476", margin: "14px 0 4px" };
const input = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #dfe5f2",
  borderRadius: 6,
  padding: "7px 9px",
  fontSize: 14,
  fontFamily: "inherit",
  color: "#1d2433",
};
const button = (primary, disabled) => ({
  height: 34,
  padding: "0 14px",
  borderRadius: 8,
  border: primary ? "none" : "1px solid #dfe5f2",
  background: disabled ? "#c9d3e6" : primary ? BLUE : "#fff",
  color: primary ? "#fff" : "#1d2433",
  fontSize: 13,
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: disabled ? "default" : "pointer",
  whiteSpace: "nowrap",
});

const dateText = (iso) => {
  if (!iso) return "";
  const d = new Date(/Z|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + "Z");
  return isNaN(d) ? "" : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

const postName = (p) =>
  p.title || (SECTIONS.map((s) => (p[s.key] || [])[0]).find(Boolean) || {}).title || p.message || "Untitled post";

const copyOf = (p) => ({ ...p, new: [...(p.new || [])], improved: [...(p.improved || [])], fixed: [...(p.fixed || [])] });

export default function WhatsNewAdmin({ open, onClose }) {
  const { authenticationData } = useContext(AuthContext);
  const api = authenticationData && authenticationData.ApiBaseURL;

  const [posts, setPosts] = useState([]);
  const [draft, setDraft] = useState(emptyPost());
  const [saved, setSaved] = useState(null); // the selected post as stored on the server
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null); // { kind: "ok" | "error", text }

  const dirty = JSON.stringify(draft) !== JSON.stringify(saved || emptyPost());

  useEffect(() => {
    if (!open || !api) return;
    setBusy(true);
    adminLoadPosts(api)
      .then((list) => {
        setPosts(list);
        if (list.length) select(list[0], true);
      })
      .catch((e) => setMessage({ kind: "error", text: e.message }))
      .finally(() => setBusy(false));
  }, [open, api]); // load once when opened (select is a plain function)

  const confirmLeave = () => !dirty || window.confirm("You have changes that are not saved. Leave them?");

  function select(post, force) {
    if (!force && !confirmLeave()) return;
    setDraft(copyOf(post));
    setSaved(copyOf(post));
    setMessage(null);
  }

  const startNew = () => {
    if (!confirmLeave()) return;
    setDraft(emptyPost());
    setSaved(null);
    setMessage(null);
  };

  const close = () => {
    if (confirmLeave()) onClose();
  };

  const set = (field, value) => setDraft((d) => ({ ...d, [field]: value }));
  const setItem = (section, index, field, value) =>
    setDraft((d) => ({ ...d, [section]: d[section].map((it, i) => (i === index ? { ...it, [field]: value } : it)) }));
  const addItem = (section) => setDraft((d) => ({ ...d, [section]: [...d[section], { title: "", text: "" }] }));
  const removeItem = (section, index) => setDraft((d) => ({ ...d, [section]: d[section].filter((_, i) => i !== index) }));

  const keep = (post) => {
    setPosts((list) => {
      const rest = list.filter((p) => p.id !== post.id);
      // Drafts on top, then newest published (same order as the server).
      return [post, ...rest].sort((a, b) => {
        if (!a.publishedAt !== !b.publishedAt) return a.publishedAt ? 1 : -1;
        return (b.publishedAt || "").localeCompare(a.publishedAt || "") || b.id - a.id;
      });
    });
    setDraft(copyOf(post));
    setSaved(copyOf(post));
  };

  const run = async (work, okText) => {
    setBusy(true);
    setMessage(null);
    try {
      const post = await work();
      keep(post);
      setMessage({ kind: "ok", text: okText(post) });
    } catch (e) {
      setMessage({ kind: "error", text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    run(() => adminSavePost(api, draft), (p) => (p.isPublished ? "Saved. Customers see the change now." : "Saved as a draft. Customers don't see it yet."));

  const saveAndPublish = () =>
    run(async () => {
      const post = dirty || !draft.id ? await adminSavePost(api, draft) : draft;
      return adminPublishPost(api, post.id, true);
    }, () => "Published. Customers see it at the top of What's new, with a dot on the button.");

  const unpublish = () => {
    if (!window.confirm("Hide this post from customers? You can publish it again later.")) return;
    run(() => adminPublishPost(api, draft.id, false), () => "Hidden from customers. It is kept here as a draft.");
  };

  const canSave = !busy && postHasContent(draft) && (dirty || !draft.id);

  return (
    <Modal open={!!open} onClose={close}>
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(1180px, 96vw)",
          height: "min(820px, 92vh)",
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 20px 60px rgba(0,0,0,.25)",
          display: "flex",
          flexDirection: "column",
          color: "#1d2433",
          outline: "none",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #eef1f6" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>What's new: posts</div>
            <div style={{ fontSize: 12, color: "#7a8396" }}>Updates and news shown to customers on the home screen.</div>
          </div>
          <button type="button" onClick={close} aria-label="Close" style={{ border: "none", background: "none", fontSize: 26, cursor: "pointer", color: "#5b6476" }}>
            {"×"}
          </button>
        </div>

        <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
          {/* Post list */}
          <div style={{ width: 250, borderRight: "1px solid #eef1f6", display: "flex", flexDirection: "column", minHeight: 0 }}>
            <div style={{ padding: 12 }}>
              <button type="button" onClick={startNew} disabled={busy} style={{ ...button(true, busy), width: "100%" }}>
                + New post
              </button>
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {posts.map((p) => {
                const active = p.id === draft.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => !active && select(p)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      border: "none",
                      borderLeft: "3px solid " + (active ? BLUE : "transparent"),
                      background: active ? "#f2f6ff" : "none",
                      padding: "10px 12px",
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1d2433", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {postName(p)}
                    </div>
                    <div style={{ fontSize: 11, marginTop: 2, color: p.isPublished ? "#15803d" : "#a16207" }}>
                      {p.isPublished ? "Published " + dateText(p.publishedAt) : p.publishedAt ? "Hidden" : "Draft"}
                    </div>
                  </button>
                );
              })}
              {!busy && posts.length === 0 && <div style={{ padding: 12, fontSize: 13, color: "#7a8396" }}>No posts yet.</div>}
            </div>
          </div>

          {/* Form */}
          <div style={{ flex: 1, overflowY: "auto", padding: "4px 20px 20px", minWidth: 0 }}>
            <label style={label}>Headline (optional)</label>
            <input style={input} maxLength={150} value={draft.title} placeholder="e.g. Meet our new talking characters" onChange={(e) => set("title", e.target.value)} />

            <label style={label}>Message (optional)</label>
            <textarea
              style={{ ...input, minHeight: 70, resize: "vertical" }}
              maxLength={2000}
              value={draft.message}
              placeholder="A few friendly sentences about the news."
              onChange={(e) => set("message", e.target.value)}
            />

            <label style={label}>Picture address (optional)</label>
            <input style={input} maxLength={500} value={draft.imageUrl} placeholder="https://..." onChange={(e) => set("imageUrl", e.target.value)} />

            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 2 }}>
                <label style={label}>Button link (optional)</label>
                <input style={input} maxLength={500} value={draft.linkUrl} placeholder="https://..." onChange={(e) => set("linkUrl", e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={label}>Button text</label>
                <input style={input} maxLength={60} value={draft.linkText} placeholder="Learn more" onChange={(e) => set("linkText", e.target.value)} />
              </div>
            </div>

            {SECTIONS.map((s) => (
              <div key={s.key} style={{ marginTop: 18 }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                  <div>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{s.label}</span>
                    <span style={{ fontSize: 12, color: "#7a8396", marginLeft: 8 }}>{SECTION_HINTS[s.key]}</span>
                  </div>
                  <button type="button" onClick={() => addItem(s.key)} style={{ border: "none", background: "none", color: BLUE, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                    + Add
                  </button>
                </div>
                {draft[s.key].map((item, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "flex-start" }}>
                    <input style={{ ...input, flex: 1 }} maxLength={100} value={item.title} placeholder="Short title" onChange={(e) => setItem(s.key, i, "title", e.target.value)} />
                    <textarea
                      style={{ ...input, flex: 2, minHeight: 36, resize: "vertical" }}
                      maxLength={500}
                      value={item.text}
                      placeholder="One or two plain sentences"
                      onChange={(e) => setItem(s.key, i, "text", e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => removeItem(s.key, i)}
                      aria-label="Remove"
                      title="Remove"
                      style={{ border: "none", background: "none", fontSize: 20, color: "#9aa3b5", cursor: "pointer", padding: "4px 2px" }}
                    >
                      {"×"}
                    </button>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Preview */}
          <div style={{ width: 360, borderLeft: "1px solid #eef1f6", background: "#f7f9fc", display: "flex", flexDirection: "column", minHeight: 0 }}>
            <div style={{ padding: "12px 16px 0", fontSize: 12, fontWeight: 600, color: "#7a8396" }}>PREVIEW</div>
            <div style={{ flex: 1, overflowY: "auto", margin: 12, background: "#fff", borderRadius: 10, boxShadow: "0 2px 10px rgba(0,0,0,.06)", padding: "6px 20px 20px" }}>
              <div style={{ fontSize: 20, fontWeight: 600, marginTop: 14 }}>What's new</div>
              {postHasContent(draft) ? (
                <WhatsNewPostView post={draft} />
              ) : (
                <div style={{ marginTop: 16, fontSize: 13, color: "#9aa3b5" }}>Start writing to see the post here.</div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 20px", borderTop: "1px solid #eef1f6" }}>
          <div style={{ flex: 1, fontSize: 13, color: message && message.kind === "error" ? "#b91c1c" : "#15803d" }}>
            {busy ? "Working…" : message ? message.text : draft.id ? (draft.isPublished ? "Published: customers see this post." : "Not published: customers don't see this post.") : "New post."}
          </div>
          {draft.isPublished && (
            <button type="button" onClick={unpublish} disabled={busy} style={button(false, busy)}>
              Unpublish
            </button>
          )}
          <button type="button" onClick={save} disabled={!canSave} style={button(false, !canSave)}>
            {draft.isPublished ? "Save changes" : "Save draft"}
          </button>
          {!draft.isPublished && (
            <button type="button" onClick={saveAndPublish} disabled={busy || !postHasContent(draft)} style={button(true, busy || !postHasContent(draft))}>
              Publish
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
